import { useMemo, useState } from 'react'
import {
  isResourceOfType,
  useReadServices,
  useWorkspaceStamps,
} from '@opallabs/api'
import { type IETFLanguageTag } from '@opallabs/concepts'
import { Size } from '@opallabs/gem'
import {
  Button,
  ButtonIconPosition,
  ButtonSize,
  Icon,
  Tooltip,
} from '@opallabs/lapidary'
import {
  CalendarCreatePopover,
  isWithinDateRange,
  OUTSIDE_STORY_RANGE,
  useCreateCalendarItem,
  type CalendarDateRange,
  type CreateChannelOption,
} from '../create'
import {
  channelMarkOf,
  drawChannelMark,
  type ChannelMark,
  type ServicesDocument,
} from './useWorkspaceDisplay'

/**
 * The channels a person can publish to, each with its own accounts, and what
 * its mark is drawn from.
 *
 * Both ids are the numeric ones, because both are handed straight to
 * `/content/v2`. The uuids the rest of the calendar is keyed by are no use
 * here. The mark is data rather than an element, so React Query is handed
 * something it can compare.
 */
const toChannelOptions = (
  document: ServicesDocument
): Array<Omit<CreateChannelOption, 'icon'> & { mark: ChannelMark }> => {
  const included = document.included ?? []
  const accounts = included.filter(isResourceOfType('account', 'V2'))
  const assets = included.filter(isResourceOfType('asset_reference', 'V2'))

  return document.data
    .map((service) => ({
      accounts: service.relationships.accounts.data.flatMap((reference) => {
        const account = accounts.find((each) => each.id === reference.id)
        return account === undefined
          ? []
          : [{ id: account.id, name: account.attributes.name }]
      }),
      id: service.id,
      mark: channelMarkOf(service, assets),
      name: service.attributes.name,
    }))
    .filter((channel) => channel.accounts.length > 0)
    .sort((left, right) => left.name.localeCompare(right.name))
}

export type UseCreateOnDayResult = Readonly<{
  /** The "new item" button for a day of the grid. */
  renderDayCreate: (date: Date) => React.ReactNode
  /** The same thing, sized for the day panel's footer. */
  renderPanelCreate: (date: Date) => React.ReactNode
}>

/**
 * Everything the calendar needs to make something on a day.
 *
 * Both places that offer it — the day cell and the day panel — open the same
 * form, so a person meets one way of making things however they got there.
 */
export const useCreateOnDay = ({
  getClearFilters,
  locale,
  storyDateRange,
  storyId,
  workspaceId,
  workspaceUuid,
}: {
  /** See `useCreateCalendarItem` — lets the success toast clear the filters. */
  getClearFilters?: () => (() => void) | null
  locale?: IETFLanguageTag
  /** The days the story runs for, when the calendar is scoped to one. */
  storyDateRange?: CalendarDateRange
  storyId?: string
  /** The numeric workspace id the v2 endpoints take. */
  workspaceId: string
  workspaceUuid: string
}): UseCreateOnDayResult => {
  const { data: channelData } = useReadServices({
    select: toChannelOptions,
    workspaceId,
  })
  // The mark beside each channel's name, at the size of a field's value icon.
  const channels = useMemo<CreateChannelOption[]>(
    () =>
      (channelData ?? []).map(({ mark, ...channel }) => ({
        ...channel,
        icon: drawChannelMark(mark, Size.Icon),
      })),
    [channelData]
  )

  // Only the form shows stamps, and reading them means paging `/stamps/v2` to
  // the end. One flag answers for every day on the grid: the first form to open
  // pays for the list, and the cache serves the rest.
  const [wasFormOpened, setFormOpened] = useState(false)
  const { stamps: momentStamps } = useWorkspaceStamps({
    enabled: wasFormOpened,
    stampType: 'moment',
    // A failed stamp list costs the form its stamp select, not the calendar.
    useErrorBoundary: false,
    workspaceId,
  })

  // The form says which channel it is on, because a post stamp only builds
  // content for the channel its own content is on.
  const [channelId, setChannelId] = useState<string | null>(null)
  const { stamps: contentStamps } = useWorkspaceStamps({
    // Without a channel the answer is every post stamp in the workspace, paged
    // to the end, and none of them could be applied anyway.
    enabled: wasFormOpened && channelId !== null,
    serviceId: channelId ?? undefined,
    stampType: 'post',
    useErrorBoundary: false,
    workspaceId,
  })

  const create = useCreateCalendarItem({
    getClearFilters,
    storyId,
    workspaceUuid,
  })

  const dateFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'long',
        weekday: 'long',
      }),
    [locale]
  )

  return useMemo(() => {
    const render = (
      date: Date,
      trigger: React.ReactElement,
      /** What the button says on hover, for a button that says nothing itself. */
      hoverText?: string
    ) => {
      if (!isWithinDateRange(date, storyDateRange)) {
        return (
          <Tooltip content={OUTSIDE_STORY_RANGE}>
            <span>{trigger}</span>
          </Tooltip>
        )
      }

      const form = (
        <CalendarCreatePopover
          channels={channels}
          contentStamps={contentStamps}
          date={date}
          dateLabel={dateFormat.format(date)}
          isSubmitting={create.isLoading}
          momentStamps={momentStamps}
          trigger={trigger}
          onChannelChange={setChannelId}
          onSubmit={(params) => create.mutateAsync(params)}
        />
      )

      if (hoverText === undefined) return form

      // The button anchors the popover, so the tooltip hangs off a wrapper
      // rather than taking the ref the popover needs.
      return (
        <Tooltip content={hoverText}>
          <span>{form}</span>
        </Tooltip>
      )
    }

    return {
      renderDayCreate: (date) =>
        render(
          date,
          <Button
            ariaLabel={`New item on ${dateFormat.format(date)}`}
            disabled={!isWithinDateRange(date, storyDateRange)}
            icon={<Icon ariaHidden={true} glyph="gem-add" />}
            name="calendar-day-create"
            onClick={() => setFormOpened(true)}
            // Small keeps the hover fill inside the 26px day header.
            size={ButtonSize.Small}
            variant="ghost"
          />,
          'Create'
        ),
      renderPanelCreate: (date) =>
        render(
          date,
          <Button
            buttonText="New"
            disabled={!isWithinDateRange(date, storyDateRange)}
            icon={<Icon ariaHidden={true} glyph="gem-add" />}
            iconPosition={ButtonIconPosition.leading}
            name="create-on-day"
            onClick={() => setFormOpened(true)}
            styleOverrides={{ button: { inlineSize: '100%' } }}
            variant="secondary"
          />
        ),
    }
  }, [
    channels,
    contentStamps,
    create,
    dateFormat,
    momentStamps,
    storyDateRange,
  ])
}
